import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import * as SliderPrimitive from '@radix-ui/react-slider';
import { ArrowUpRight, Check, ChevronDown, ChevronLeft, ChevronRight, GripVertical, Info, Lock, Pencil, Search, Settings, Star, Tv, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import Popover from './Popover.jsx';
import client from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  AGE_OPTIONS,
  CIRCLE_FILTERS,
  CONTENT_TYPES,
  RUNTIME_OPTIONS,
  RATING_MAX,
  RATING_MIN,
  RATING_STEP,
  fmtRating,
  multiLabel,
  servicesShowing,
  yearOptions,
} from '../filters.js';

/* ---------------------------------------------------------------- gating -- */

/**
 * Every optional control asks `gate(key)` whether it may be used.
 *  - capability missing → disabled with "Not available yet" (never silently ignored)
 *  - entitlement locked → shows a lock and calls onUpgrade (existing Premiere flow)
 */
export function makeGate(capabilities = {}, entitlements = {}) {
  return (key) => {
    if (entitlements.lockedFilters?.includes(key)) {
      return { disabled: false, locked: true, reason: entitlements.lockedReason ?? 'Premiere feature' };
    }
    if (capabilities[key] !== true) {
      return { disabled: true, locked: false, reason: capabilities[`${key}Reason`] ?? 'Not available yet' };
    }
    return { disabled: false, locked: false, reason: null };
  };
}

function GateNote({ state }) {
  if (!state.reason) return null;
  return (
    <span className="ml-auto flex shrink-0 items-center gap-1 text-[11px] font-medium text-sr-faint">
      {state.locked && <Lock size={11} className="text-sr-gold" aria-hidden />}
      {state.reason}
    </span>
  );
}

/* ------------------------------------------------------------- checkbox -- */

export function Checkbox({ checked, onChange, disabled, children, note, testId }) {
  return (
    <label
      className={`flex min-h-[21px] items-center gap-[9px] py-[1px] text-[13.5px] leading-tight ${
        disabled ? 'cursor-not-allowed opacity-45' : 'cursor-pointer'
      }`}
    >
      <input
        type="checkbox"
        className="peer sr-only"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testId}
      />
      <span
        aria-hidden
        className="grid h-[14px] w-[14px] shrink-0 place-items-center rounded-[3px] border border-sr-check bg-transparent text-sr-bg peer-checked:border-sr-gold peer-checked:bg-sr-gold peer-focus-visible:ring-2 peer-focus-visible:ring-sr-gold peer-focus-visible:ring-offset-1 peer-focus-visible:ring-offset-sr-panel"
      >
        {checked && <Check size={11} strokeWidth={3.5} />}
      </span>
      <span className="text-sr-text">{children}</span>
      {note}
    </label>
  );
}

/* --------------------------------------------------------- content type -- */

export function ContentTypeControl({ filters, setTypes }) {
  return (
    <div>
      {CONTENT_TYPES.map((t) => (
        <Checkbox
          key={t.id}
          checked={filters.types.includes(t.id)}
          onChange={(on) => setTypes(on ? [...filters.types, t.id] : filters.types.filter((x) => x !== t.id))}
        >
          {t.label}
        </Checkbox>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------- genres -- */

export function GenreChecklist({ genres, selected, onToggle, onClear }) {
  return (
    <div>
      <div className="mb-2 flex items-center justify-between text-[12px] text-sr-muted">
        <span>{selected.length ? `${selected.length} selected` : 'Any genre'}</span>
        {selected.length > 0 && (
          <button type="button" className="text-sr-gold hover:underline" onClick={onClear}>
            Clear
          </button>
        )}
      </div>
      <div className="grid max-h-[260px] grid-cols-2 gap-x-3 overflow-y-auto pr-1">
        {genres.map((g) => (
          <Checkbox key={g.id} checked={selected.includes(g.id)} onChange={() => onToggle(g.id)}>
            {g.name}
          </Checkbox>
        ))}
      </div>
    </div>
  );
}

const selectShell =
  'flex h-[30px] w-full items-center justify-between rounded-[8px] border border-sr-field-line bg-sr-field px-[11px] text-left text-[14px] text-sr-text';

/** Sidebar "All Genres" dropdown — opens a multi-select checklist. */
export function GenreDropdown({ genres, selected, onToggle, onClear, placeholder }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      width={300}
      trigger={(props) => (
        <button type="button" {...props} className={selectShell}>
          <span className="truncate">{placeholder ?? multiLabel(selected, genres, 'All Genres', 'Genres')}</span>
          <ChevronDown size={16} className="shrink-0 text-sr-text" aria-hidden />
        </button>
      )}
    >
      <GenreChecklist genres={genres} selected={selected} onToggle={onToggle} onClear={onClear} />
    </Popover>
  );
}

/* -------------------------------------------------------------- services -- */

export function DefaultProviderTile({ provider }) {
  return <span className="truncate px-1 text-[11px] font-bold tracking-tight text-sr-text">{provider.name}</span>;
}

/** Renders a service name next to whatever tile it's anchored to via a
 * fixed-position portal into document.body — a scroll container's
 * `overflow` can never clip it, because it isn't a descendant of that
 * container in the DOM at all (unlike the old CSS-only hover tooltip,
 * which clipped inside the Browse-all panel and at grid edges). */
export function FloatingTooltip({ anchorRef, visible, children }) {
  const [pos, setPos] = useState(null);
  useEffect(() => {
    if (!visible) {
      setPos(null);
      return undefined;
    }
    const update = () => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const left = Math.min(Math.max(r.left + r.width / 2, 80), window.innerWidth - 80);
      // Clamp the top too — without it, a tile near the very top of the
      // viewport (e.g. under a sticky nav) would place the tooltip, which
      // floats ABOVE the tile, at a negative/off-screen coordinate.
      const top = Math.max(r.top - 8, 28);
      setPos({ top, left });
    };
    update();
    // `true` = capture phase, so this fires for scrolls on ANY ancestor
    // scroll container (the Browse-all panel, a Popover's own scroll area),
    // not just window — scroll events don't bubble, but capture-phase
    // listeners still see them on the way down to the target.
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [visible, anchorRef]);
  if (!visible || !pos || typeof document === 'undefined') return null;
  return createPortal(
    <span
      role="tooltip"
      style={{ position: 'fixed', top: pos.top, left: pos.left, transform: 'translate(-50%, -100%)' }}
      className="pointer-events-none z-[999] whitespace-nowrap rounded-[6px] bg-black px-2 py-1 text-[11px] font-semibold text-white shadow-lg"
    >
      {children}
    </span>,
    document.body,
  );
}

function ServiceTile({ provider, on, onToggle, onRemove, renderProviderBadge, testid }) {
  const tile =
    'relative flex h-[36px] min-w-0 items-center justify-center rounded-[7px] border border-sr-tile-line bg-sr-tile transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sr-gold';
  const btnRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  return (
    <div className="group relative">
      <button
        ref={btnRef}
        type="button"
        aria-label={onRemove ? `Remove ${provider.name} from favorites` : provider.name}
        aria-pressed={onRemove ? undefined : on}
        data-testid={testid}
        onClick={() => (onRemove ? onRemove(provider.id) : onToggle(provider.id))}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        // Only keyboard-style focus (Tab) should keep the tooltip open —
        // a mouse click also focuses the button, and without this check the
        // tooltip stayed stuck open after clicking until focus moved away,
        // since mouseleave alone doesn't clear a focus-driven `focused` flag.
        onFocus={(e) => setFocused(e.target.matches(':focus-visible'))}
        onBlur={() => setFocused(false)}
        className={`w-full ${tile} ${on ? 'border-sr-gold shadow-[0_0_0_1.5px_#f6c026]' : 'hover:border-sr-muted'}`}
      >
        {/* The logo clips to the tile's rounded corners via its OWN
            overflow-hidden wrapper — overflow-hidden can't live on the
            button itself without also clipping the check badge in its corner. */}
        <span className="flex h-full w-full items-center justify-center overflow-hidden rounded-[7px]">
          {renderProviderBadge ? renderProviderBadge({ provider, variant: 'tile', selected: on }) : <DefaultProviderTile provider={provider} />}
        </span>
      </button>
      {onRemove && (
        <span
          aria-hidden
          data-testid={`${testid}-remove`}
          className="pointer-events-none absolute -left-[5px] -top-[5px] z-10 grid h-[16px] w-[16px] place-items-center rounded-full border border-sr-tile-line bg-sr-panel text-sr-text"
        >
          <X size={10} strokeWidth={3} />
        </span>
      )}
      {on && !onRemove && (
        <span
          aria-hidden
          data-testid={`${testid}-on`}
          className="pointer-events-none absolute -right-[5px] -top-[5px] z-10 grid h-[16px] w-[16px] place-items-center rounded-full border border-sr-gold bg-sr-gold text-sr-bg"
        >
          <Check size={10} strokeWidth={3} />
        </span>
      )}
      <FloatingTooltip anchorRef={btnRef} visible={hovered || focused}>
        {provider.name}
      </FloatingTooltip>
    </div>
  );
}

function TileGrid({ providers, selected, onToggle, onRemove, renderProviderBadge, emptyText, testidPrefix = 'sr-service-tile' }) {
  if (providers.length === 0) return emptyText ? <p className="text-[12px] text-sr-muted">{emptyText}</p> : null;
  return (
    <div className="grid grid-cols-4 gap-x-[12px] gap-y-[8px]">
      {providers.map((p) => (
        <ServiceTile
          key={p.id}
          provider={p}
          on={selected.includes(p.id)}
          onToggle={onToggle}
          onRemove={onRemove}
          renderProviderBadge={renderProviderBadge}
          testid={`${testidPrefix}-${p.id}`}
        />
      ))}
    </div>
  );
}

/** One Favorites tile while reordering (DECISIONS #22): drag on desktop
 * (native HTML5 DnD — no library needed for a list this short), plus
 * always-visible ‹ › move buttons so the same reorder works by click/tap
 * and by keyboard, not just a mouse drag. Premiere-only; non-Premiere
 * members keep the plain tap-to-remove `ServiceTile` via `TileGrid` above. */
function ReorderableFavoriteTile({ provider, index, total, dragging, onRemove, onMove, onDragStart, onDragOver, onDrop, onDragEnd, renderProviderBadge, testid }) {
  return (
    <div
      className={`group relative transition-opacity ${dragging ? 'opacity-40' : ''}`}
      draggable
      onDragStart={(e) => {
        // Firefox requires dataTransfer.setData to be called before it will
        // start an HTML5 drag at all; Chromium/Safari don't need it but
        // tolerate it fine.
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', String(provider.id));
        onDragStart(index);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        onDragOver(index);
      }}
      onDrop={(e) => {
        e.preventDefault();
        onDrop(index);
      }}
      onDragEnd={onDragEnd}
    >
      <button
        type="button"
        aria-label={`Remove ${provider.name} from favorites`}
        data-testid={testid}
        onClick={() => onRemove(provider.id)}
        className="relative flex h-[36px] w-full min-w-0 cursor-grab items-center justify-center rounded-[7px] border border-sr-tile-line bg-sr-tile transition-shadow hover:border-sr-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sr-gold active:cursor-grabbing"
      >
        <span className="flex h-full w-full items-center justify-center overflow-hidden rounded-[7px]">
          {renderProviderBadge ? renderProviderBadge({ provider, variant: 'tile', selected: false }) : <DefaultProviderTile provider={provider} />}
        </span>
      </button>
      <span aria-hidden className="pointer-events-none absolute -left-[5px] -top-[5px] z-10 grid h-[16px] w-[16px] place-items-center rounded-full border border-sr-tile-line bg-sr-panel text-sr-text">
        <X size={10} strokeWidth={3} />
      </span>
      <span aria-hidden className="pointer-events-none absolute -right-[5px] -top-[5px] z-10 grid h-[16px] w-[16px] place-items-center rounded-full border border-sr-tile-line bg-sr-panel text-sr-muted">
        <GripVertical size={10} strokeWidth={2.5} />
      </span>
      <div className="mt-[2px] flex justify-center gap-[6px]" data-testid={`${testid}-move`}>
        <button
          type="button"
          aria-label={`Move ${provider.name} earlier in Favorites`}
          disabled={index === 0}
          onClick={() => onMove(index, index - 1)}
          data-testid={`${testid}-move-left`}
          className="grid h-[22px] w-[22px] place-items-center rounded-[5px] text-sr-muted hover:bg-sr-tile hover:text-sr-gold focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-sr-gold disabled:opacity-25 disabled:hover:bg-transparent disabled:hover:text-sr-muted"
        >
          <ChevronLeft size={13} aria-hidden />
        </button>
        <button
          type="button"
          aria-label={`Move ${provider.name} later in Favorites`}
          disabled={index === total - 1}
          onClick={() => onMove(index, index + 1)}
          data-testid={`${testid}-move-right`}
          className="grid h-[22px] w-[22px] place-items-center rounded-[5px] text-sr-muted hover:bg-sr-tile hover:text-sr-gold focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-sr-gold disabled:opacity-25 disabled:hover:bg-transparent disabled:hover:text-sr-muted"
        >
          <ChevronRight size={13} aria-hidden />
        </button>
      </div>
    </div>
  );
}

function ReorderableFavoritesGrid({ providers, onReorder, onRemove, renderProviderBadge, testidPrefix = 'sr-service-tile-favorite' }) {
  const [order, setOrder] = useState(() => providers.map((p) => p.id));
  const [dragIndex, setDragIndex] = useState(null);
  const orderAtDragStartRef = useRef(null);
  useEffect(() => {
    // Re-sync from the saved list whenever it changes from OUTSIDE this
    // component (e.g. a favorite removed) — but never mid-drag, where a
    // prop update would just be this component's own optimistic reorder
    // echoing straight back and resetting the drag.
    if (dragIndex === null) setOrder(providers.map((p) => p.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providers]);
  if (providers.length === 0) return null;
  const byId = new Map(providers.map((p) => [p.id, p]));
  const ordered = order.map((id) => byId.get(id)).filter(Boolean);
  // The ‹ › buttons are each a complete, discrete action, so they commit
  // (and save) immediately. A drag is one continuous gesture spanning many
  // dragover events — reordering `order` locally on every one of those and
  // only saving once, on drop, avoids firing a save per pixel moved.
  const moveAndSave = (from, to) => {
    if (to < 0 || to >= order.length || from === to) return;
    const next = order.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setOrder(next);
    onReorder(next);
  };
  const sameOrder = (a, b) => a.length === b.length && a.every((id, i) => id === b[i]);
  return (
    <div className="grid grid-cols-4 gap-x-[12px] gap-y-[14px]">
      {ordered.map((p, i) => (
        <ReorderableFavoriteTile
          key={p.id}
          provider={p}
          index={i}
          total={ordered.length}
          dragging={dragIndex === i}
          onRemove={onRemove}
          onMove={moveAndSave}
          onDragStart={(idx) => {
            orderAtDragStartRef.current = order;
            setDragIndex(idx);
          }}
          onDragOver={(idx) => {
            if (dragIndex === null || dragIndex === idx) return;
            setOrder((cur) => {
              const next = cur.slice();
              const [moved] = next.splice(dragIndex, 1);
              next.splice(idx, 0, moved);
              return next;
            });
            setDragIndex(idx);
          }}
          onDrop={() => setDragIndex(null)}
          onDragEnd={(e) => {
            setDragIndex(null);
            // Dropped outside any valid target, or cancelled with Esc —
            // dropEffect stays 'none' either way. Revert instead of
            // committing whatever the pointer happened to pass over.
            if (e.dataTransfer && e.dataTransfer.dropEffect === 'none') {
              setOrder(orderAtDragStartRef.current || order);
              return;
            }
            if (!sameOrder(order, providers.map((prov) => prov.id))) onReorder(order);
          }}
          renderProviderBadge={renderProviderBadge}
          testid={`${testidPrefix}-${p.id}`}
        />
      ))}
    </div>
  );
}

/** Small uppercase section label shared by every service group (Your
 * services / Favorite services / Also showing / Major / Free) — one place to restyle them all. */
function GroupLabel({ children }) {
  return <p className="mb-[6px] text-[11px] font-semibold uppercase tracking-wide text-sr-faint">{children}</p>;
}

// Pinning was removed (DECISIONS #16) — it read as "these are filtering".
// Clear the old browser-only pin list once so it doesn't linger.
try {
  if (typeof window !== 'undefined') localStorage.removeItem('sr_pinned_services');
} catch {
  // blocked storage — nothing to clear
}

// Favorite services (DECISIONS #17): extra services a member likes to browse
// beyond the ones they subscribe to. Saved on the ACCOUNT
// (`/screening-room/favorite-services`) so web and mobile always match.
// Sidebar, toolbar popover and mobile drawer can all be mounted at once, so a
// single module-level array plus a listener set keeps them in step.
const MAX_FAVORITE_SERVICES = 24;
let favoritesStore = [];
let favoritesRequested = false;
let favoritesSeq = 0; // bumped on every edit, so a slow/failed save only
// reverts if nothing newer has been saved in the meantime (DECISIONS #17).
const favoritesListeners = new Set();

function setFavoritesStore(next) {
  favoritesStore = next;
  favoritesListeners.forEach((fn) => fn(next));
}

// Call when the signed-in account changes (login, signup, or logout) — the
// module-level cache above is per-page-load, not per-account, so without
// this a second account signing in during the same SPA session (no full
// page reload) would briefly see the previous account's favorites until its
// own GET finally lands, and an edit in that window would overwrite the new
// account with the old account's list.
export function resetPinnedServicesCache() {
  favoritesRequested = false;
  setFavoritesStore([]);
}

// Saves the new list optimistically, then un-does the local change and warns
// the member if the server rejected it — without this, a favorite could
// silently vanish on the next reload with no sign anything went wrong. Only
// reverts if this is still the MOST RECENT edit — otherwise an older,
// slower request failing after a newer one already saved successfully would
// wipe out that newer change instead of just its own.
function saveFavorites(next, previous) {
  const mySeq = ++favoritesSeq;
  client.put('/screening-room/favorite-services', { favorites: next }).catch(() => {
    if (mySeq === favoritesSeq) setFavoritesStore(previous);
    toast.error("Couldn't save your favorite services — please try again.");
  });
}

export function useFavoriteServices() {
  const [favorites, setLocal] = useState(favoritesStore);
  useEffect(() => {
    setLocal(favoritesStore);
    favoritesListeners.add(setLocal);
    if (!favoritesRequested) {
      favoritesRequested = true;
      client
        .get('/screening-room/favorite-services')
        .then(({ data }) => setFavoritesStore(Array.isArray(data?.favorites) ? data.favorites : []))
        .catch(() => {
          favoritesRequested = false; // try again on the next mount
        });
    }
    return () => favoritesListeners.delete(setLocal);
  }, []);
  const addFavorites = (ids) => {
    const fresh = ids.filter((id) => !favoritesStore.includes(id));
    if (!fresh.length) return;
    const previous = favoritesStore;
    const next = [...favoritesStore, ...fresh].slice(-MAX_FAVORITE_SERVICES);
    setFavoritesStore(next);
    saveFavorites(next, previous);
  };
  const removeFavorite = (id) => {
    const previous = favoritesStore;
    const next = favoritesStore.filter((x) => x !== id);
    setFavoritesStore(next);
    saveFavorites(next, previous);
  };
  // Premiere-only (DECISIONS #22): drag or move a favorite to a new spot in
  // the list. `next` is the full reordered id array — same save path as
  // add/remove, so a failed save reverts and warns exactly the same way.
  const reorderFavorites = (next) => {
    const previous = favoritesStore;
    setFavoritesStore(next);
    saveFavorites(next, previous);
  };
  return { favorites, addFavorites, removeFavorite, reorderFavorites };
}

/**
 * Platform tiles. When the member has set their own services, shows those
 * first (so the default view stays "just mine"), PLUS a "Browse all
 * services" link that opens a separate alphabetical, searchable dialog for
 * picking any other provider as a one-off filter — never expanded inline,
 * so the sidebar never grows to fit every TMDB-known provider and there's
 * only ever one services search box, not a second one buried in a
 * disclosure. With no services set, shows the full regional catalog
 * grouped into Major / Free, same source and grouping as the profile's
 * streaming-services picker (DECISIONS.md #4).
 */
/* Decorative film-reel art for the "Currently Showing" banner, drawn in code
   (no image asset): a soft gold glow plus a strip of sprocket holes. */
export function ReelArt() {
  return (
    <span aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden rounded-[11px]">
      <span className="absolute inset-0 bg-[radial-gradient(120%_140%_at_100%_0%,rgba(246,192,38,0.30),rgba(246,192,38,0.06)_45%,transparent_70%)]" />
      <span className="absolute -right-[18px] top-[-30%] h-[160%] w-[54px] rotate-[24deg] rounded-[6px] border border-sr-gold/35 bg-[repeating-linear-gradient(180deg,rgba(246,192,38,0.55)_0_7px,transparent_7px_15px)] opacity-60 [mask-image:linear-gradient(to_left,black,transparent)]" />
    </span>
  );
}

function ShowingChip({ label, icon, onRemove, testid }) {
  return (
    <span data-testid={testid} className="inline-flex max-w-full items-center gap-[6px] rounded-full border border-sr-gold/60 bg-sr-panel/80 py-[3px] pl-[8px] pr-[4px] text-[12.5px] font-semibold text-sr-text">
      {icon}
      <span className="truncate">{label}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${label}`}
        data-testid={testid ? `${testid}-remove` : undefined}
        className="grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full text-sr-muted hover:text-sr-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sr-gold"
      >
        <X size={12} strokeWidth={2.6} aria-hidden />
      </button>
    </span>
  );
}

function RowHeader({ title, subtitle, action, extra }) {
  return (
    <div className="mb-[8px] flex items-start justify-between gap-2 border-l-[3px] border-sr-gold pl-[9px]">
      <div className="min-w-0">
        <p className="m-0 flex flex-wrap items-center gap-2 text-[14px] font-semibold text-sr-text">
          {title}
          {extra}
        </p>
        {subtitle && <p className="m-0 text-[11.5px] text-sr-muted">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

function RowAction({ onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="flex shrink-0 items-center gap-1 text-[12px] font-semibold text-sr-gold hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sr-gold"
    >
      {children}
    </button>
  );
}

function EmptyCard({ icon, title, body, cta, onCta, testid }) {
  return (
    <div data-testid={testid} className="rounded-[10px] border border-dashed border-sr-tile-line px-[14px] py-[14px] text-center">
      <div className="mx-auto mb-[6px] grid h-[30px] w-[30px] place-items-center text-sr-muted">{icon}</div>
      <p className="m-0 text-[14px] font-semibold text-sr-text">{title}</p>
      <p className="m-0 mt-[2px] text-[12px] text-sr-muted">{body}</p>
      {cta && (
        <button
          type="button"
          onClick={onCta}
          data-testid={`${testid}-cta`}
          className="mt-[10px] rounded-[8px] border border-sr-gold px-[14px] py-[6px] text-[13px] font-bold text-sr-gold hover:bg-sr-gold/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sr-gold"
        >
          {cta}
        </button>
      )}
    </div>
  );
}

function Switch({ on, disabled, label }) {
  return (
    <span
      aria-hidden
      className={`relative inline-flex h-[22px] w-[40px] shrink-0 items-center rounded-full border transition-colors ${
        on ? 'border-sr-gold bg-sr-gold' : 'border-sr-tile-line bg-sr-tile'
      } ${disabled ? 'opacity-60' : ''}`}
      title={label}
    >
      <span className={`absolute h-[16px] w-[16px] rounded-full bg-white shadow transition-transform ${on ? 'translate-x-[20px]' : 'translate-x-[3px]'}`} />
    </span>
  );
}

/**
 * Streaming Services section (DECISIONS #15–#18). Renders in the sidebar,
 * the mobile drawer and the toolbar's Services popover.
 *  1. "Currently Showing" — what is ACTUALLY filtering, as removable chips
 *     (built from servicesShowing(), the same rule as every other summary).
 *  2. My Services switch — Premiere; locked for free members; disabled with a
 *     prompt when no subscriptions are saved yet.
 *  3. My Subscriptions — what the member pays for (+ Manage → Settings).
 *  4. Favorites — extra services they browse (+ Edit in place).
 *  5. Also showing — anything filtering that isn't in 3 or 4.
 *  6. Browse all — checklist dialog.
 * In Theaters replaces all of it with a "Not used for In Theaters" note.
 */
export function ServiceTiles({
  major = [],
  all = [],
  userProviderIds = [],
  onManageServices,
  selected,
  onToggle,
  renderProviderBadge,
  availabilityFilter = {},
  onUnpinMyService,
  onSetProviders,
  onApplyServices,
  onLocked,
  theatersOnly = false,
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFavorites, setEditingFavorites] = useState(false);
  const { favorites, addFavorites, removeFavorite, reorderFavorites } = useFavoriteServices();
  const { enabled: onMyServices = false, setEnabled, isPremium = true } = availabilityFilter;
  const hasOwn = userProviderIds.length > 0;
  const mine = hasOwn ? all.filter((p) => userProviderIds.includes(p.id)) : [];

  const showing = servicesShowing({ enabled: onMyServices, selectedIds: selected, userProviderIds, providers: all });
  const visualSelected = showing.activeIds;

  // Tapping a tile that's only "on" because of the toggle means "not ALL of
  // my services any more" — turn the toggle off and keep the rest (#13).
  const handleToggle = (id) => {
    if (onMyServices && userProviderIds.includes(id) && !selected.includes(id) && onUnpinMyService) onUnpinMyService(id);
    else onToggle(id);
  };

  const favoriteProviders = favorites
    .filter((id) => !userProviderIds.includes(id))
    .map((id) => all.find((p) => p.id === id))
    .filter(Boolean);
  const visibleIds = new Set([...(hasOwn ? mine : major), ...favoriteProviders].map((p) => p.id));
  const added = selected.filter((id) => !visibleIds.has(id)).map((id) => all.find((p) => p.id === id)).filter(Boolean);

  // Edit mode has nothing to turn itself off once its row disappears.
  useEffect(() => {
    if (favoriteProviders.length === 0 && editingFavorites) setEditingFavorites(false);
  }, [favoriteProviders.length, editingFavorites]);

  const applyFromBrowse = (ids) => {
    addFavorites(ids.filter((id) => !userProviderIds.includes(id)));
    onApplyServices?.(ids);
    setDialogOpen(false);
  };

  // Removing a "Currently Showing" chip.
  const removeBase = () => {
    if (!showing.base) return;
    if (onMyServices) setEnabled?.(false);
    onSetProviders?.(selected.filter((id) => !showing.base.ids.includes(id)));
  };
  const removeExtra = (id) => onSetProviders?.(selected.filter((x) => x !== id));

  if (theatersOnly) {
    return (
      <div data-testid="sr-services-theaters" className="flex gap-[10px] rounded-[10px] border border-sr-tile-line px-[12px] py-[12px]">
        <Info size={18} className="mt-[1px] shrink-0 text-sr-muted" aria-hidden />
        <div>
          <p className="m-0 text-[14px] font-semibold text-sr-text">Not used for In Theaters</p>
          <p className="m-0 text-[12px] text-sr-muted">Streaming services don&apos;t apply when searching for titles in theaters.</p>
        </div>
      </div>
    );
  }

  const logoOf = (id) => {
    const p = all.find((x) => x.id === id);
    if (!p || !renderProviderBadge) return null;
    return (
      <span className="grid h-[18px] w-[18px] shrink-0 place-items-center overflow-hidden rounded-[4px]">
        {renderProviderBadge({ provider: p, variant: 'tile', selected: true })}
      </span>
    );
  };
  const ownCountLabel =
    !onMyServices && showing.base?.kind === 'subset' ? `${showing.base.ids.length} of ${mine.length}` : `${mine.length}`;

  return (
    <div className="space-y-[16px]">
      {/* 1. Currently Showing */}
      <div data-testid="sr-services-showing" className="relative rounded-[11px] border border-sr-gold/55 bg-sr-gold/[.07] px-[14px] pb-[12px] pt-[10px]">
        <ReelArt />
        <div className="relative">
          <p className="m-0 font-sr-display text-[20px] font-semibold leading-tight text-sr-text">Currently Showing</p>
          <p data-testid="sr-services-showing-value" className="m-0 mb-[8px] text-[12.5px] text-[#f3dc9a]">
            {showing.subtitle}
          </p>
          {(showing.base || showing.extras.length > 0) && (
            <div className="flex flex-wrap gap-[6px]">
              {showing.base && (
                <ShowingChip
                  testid="sr-showing-chip-base"
                  label={showing.base.label}
                  icon={showing.base.kind === 'single' ? logoOf(showing.base.ids[0]) : <Tv size={14} className="text-sr-gold" aria-hidden />}
                  onRemove={removeBase}
                />
              )}
              {showing.extras.map((e) => (
                <ShowingChip key={e.id} testid={`sr-showing-chip-${e.id}`} label={e.name} icon={logoOf(e.id)} onRemove={() => removeExtra(e.id)} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 2. My Services switch */}
      {!isPremium ? (
        <div
          role="button"
          tabIndex={0}
          onClick={onLocked}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onLocked?.();
            }
          }}
          data-testid="sr-my-services-switch-locked"
          className="flex w-full cursor-pointer items-start gap-[10px] rounded-[10px] border border-sr-gold/45 bg-sr-gold/[.06] px-[12px] py-[10px] text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sr-gold"
        >
          <Lock size={18} className="mt-[2px] shrink-0 text-sr-gold" aria-hidden />
          <Switch on={false} disabled label="Locked" />
          <span className="min-w-0">
            <span className="flex flex-wrap items-center gap-2 text-[14px] font-semibold text-sr-text">
              My Services
              <span className="rounded-[5px] bg-sr-gold px-[6px] py-[1px] text-[10.5px] font-bold text-sr-bg">Premiere Only</span>
            </span>
            <span data-testid="sr-services-combine-hint" className="mt-[3px] block text-[12.5px] font-semibold leading-snug text-[#f3dc9a]">
              Select one service at a time or combine services with a{' '}
              <Link
                to="/app/upgrade"
                onClick={(e) => e.stopPropagation()}
                data-testid="sr-services-combine-hint-link"
                className="font-extrabold text-sr-gold underline underline-offset-2 hover:text-[#ffd75e]"
              >
                Premiere membership
              </Link>
              .
            </span>
          </span>
        </div>
      ) : !hasOwn ? (
        <div data-testid="sr-my-services-switch-empty" className="flex items-center gap-[10px] rounded-[10px] border border-sr-tile-line px-[12px] py-[10px]">
          <Switch on={false} disabled label="Add your subscriptions first" />
          <span className="text-[13px] text-sr-muted">
            <span className="block font-semibold text-sr-text">My Services</span>
            Add your subscriptions to use My Services.
          </span>
        </div>
      ) : (
        <button
          type="button"
          role="switch"
          aria-checked={onMyServices}
          onClick={() => setEnabled?.(!onMyServices)}
          data-testid="sr-my-services-switch"
          aria-describedby="sr-my-services-info"
          className="flex w-full items-center gap-[10px] rounded-[10px] border border-sr-tile-line px-[12px] py-[10px] text-left hover:border-sr-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sr-gold"
        >
          <Switch on={onMyServices} label="My Services" />
          <span className="min-w-0">
            <span className="flex items-center gap-[5px]">
              <span className="text-[14px] font-semibold text-sr-text">My Services</span>
              {/* An `svg` has no native tooltip of its own — a `title`
                  attribute directly on it is silently ignored by every
                  browser. Wrapping it in a span (an ordinary HTML element)
                  is what actually produces the hover tooltip. A hover-only
                  tooltip also never reaches touch or keyboard/screen-reader
                  users, so the same text is additionally wired in as the
                  switch button's aria-describedby. */}
              <span title="Uses all of your saved subscriptions together as the base for results.">
                <Info size={12} className="shrink-0 text-sr-muted" aria-hidden="true" />
              </span>
            </span>
            <span id="sr-my-services-info" className="block text-[12px] text-sr-muted">Use all {mine.length} of your subscriptions as the base.</span>
          </span>
        </button>
      )}

      {/* 3. My Subscriptions */}
      <div data-testid="sr-my-subscriptions">
        <RowHeader
          title={`My Subscriptions (${ownCountLabel})`}
          subtitle="Services you currently pay for"
          action={
            onManageServices && (
              <RowAction onClick={onManageServices} testid="sr-manage-services">
                <Settings size={13} aria-hidden /> Manage <ArrowUpRight size={13} aria-hidden />
              </RowAction>
            )
          }
        />
        {hasOwn ? (
          <TileGrid providers={mine} selected={visualSelected} onToggle={handleToggle} renderProviderBadge={renderProviderBadge} testidPrefix="sr-service-tile-mine" />
        ) : (
          <>
            <EmptyCard
              testid="sr-my-subscriptions-empty"
              icon={<Tv size={26} aria-hidden />}
              title="No subscriptions saved"
              body="Add the streaming services you pay for to get the best results."
              cta={onManageServices ? 'Add My Services' : null}
              onCta={onManageServices}
            />
            {major.length > 0 && (
              <div className="mt-[12px]">
                <GroupLabel>Popular services</GroupLabel>
                <TileGrid providers={major} selected={visualSelected} onToggle={handleToggle} renderProviderBadge={renderProviderBadge} testidPrefix="sr-service-tile-major" />
              </div>
            )}
          </>
        )}
      </div>

      {/* 4. Favorites */}
      <div data-testid="sr-favorite-services">
        <RowHeader
          title={`Favorites (${favoriteProviders.length})`}
          subtitle={editingFavorites && availabilityFilter.isPremium && favoriteProviders.length > 1 ? 'Drag, or use ‹ ›, to reorder. Tap a tile to remove it.' : 'Other services you like to browse'}
          action={
            favoriteProviders.length > 0 && (
              <RowAction onClick={() => setEditingFavorites((v) => !v)} testid="sr-favorite-services-edit">
                {!editingFavorites && <Pencil size={12} aria-hidden />} {editingFavorites ? 'Done' : 'Edit'}
              </RowAction>
            )
          }
        />
        {favoriteProviders.length > 0 ? (
          <>
            {editingFavorites && availabilityFilter.isPremium ? (
              <ReorderableFavoritesGrid
                providers={favoriteProviders}
                onReorder={reorderFavorites}
                onRemove={removeFavorite}
                renderProviderBadge={renderProviderBadge}
                testidPrefix="sr-service-tile-favorite"
              />
            ) : (
              <>
                <TileGrid
                  providers={favoriteProviders}
                  selected={visualSelected}
                  onToggle={handleToggle}
                  onRemove={editingFavorites ? removeFavorite : undefined}
                  renderProviderBadge={renderProviderBadge}
                  testidPrefix="sr-service-tile-favorite"
                />
                {editingFavorites && <p className="m-0 mt-[6px] text-[12px] text-sr-muted">Tap a service to remove it from your favorites.</p>}
              </>
            )}
          </>
        ) : (
          <EmptyCard
            testid="sr-favorite-services-empty"
            icon={<Star size={24} aria-hidden />}
            title="No favorites yet"
            body="Add other services you like to browse from the full list."
            cta={`Browse All Services (${all.length})`}
            onCta={() => setDialogOpen(true)}
          />
        )}
      </div>

      {/* 5. Also showing */}
      {added.length > 0 && (
        <div>
          <GroupLabel>Also showing</GroupLabel>
          <TileGrid providers={added} selected={visualSelected} onToggle={handleToggle} renderProviderBadge={renderProviderBadge} testidPrefix="sr-service-tile-added" />
        </div>
      )}

      {/* 6. Browse all — omitted when Favorites is empty, since its own
          empty-state card above already offers this exact same action;
          showing both was two identical "Browse All Services" CTAs in a row. */}
      {favoriteProviders.length > 0 && (
        <button
          type="button"
          data-testid="sr-browse-all-services-toggle"
          onClick={() => setDialogOpen(true)}
          className="flex w-full items-center gap-[10px] rounded-[9px] border border-sr-gold/60 px-[12px] py-[8px] text-left text-[14px] text-sr-text hover:bg-sr-gold/[.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sr-gold"
        >
          <Search size={16} className="shrink-0 text-sr-muted" aria-hidden />
          <span className="flex-1">Browse All Services ({all.length})</span>
          <ChevronDown size={16} className="-rotate-90 text-sr-gold" aria-hidden />
        </button>
      )}
      <BrowseAllServicesDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        providers={all}
        selected={visualSelected}
        multiple={isPremium}
        onApply={applyFromBrowse}
        renderProviderBadge={renderProviderBadge}
      />
    </div>
  );
}

/**
 * "Browse all services" — a separate, alphabetical, searchable checklist.
 * Premiere members tick as many services as they like; free members pick one
 * (DECISIONS #15). Nothing changes until "Show …" is pressed, so a member can
 * build the whole mix first. Picked services that aren't the member's own are
 * remembered as Favorite services (DECISIONS #17).
 */
function BrowseAllServicesDialog({ open, onClose, providers, selected, multiple, onApply, renderProviderBadge }) {
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState(selected);
  useEffect(() => {
    if (open) {
      setSearch('');
      setDraft(multiple ? selected : selected.slice(0, 1));
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const sorted = [...providers].sort((a, b) => a.name.localeCompare(b.name));
  const q = search.trim().toLowerCase();
  const filtered = q ? sorted.filter((p) => p.name.toLowerCase().includes(q)) : sorted;
  const tick = (id) =>
    setDraft((d) => (multiple ? (d.includes(id) ? d.filter((x) => x !== id) : [...d, id]) : d[0] === id ? [] : [id]));
  const draftName = draft.length === 1 ? providers.find((p) => p.id === draft[0])?.name : null;
  const applyLabel = draft.length === 0 ? 'Show all services' : draftName ? `Show ${draftName}` : `Show ${draft.length} services`;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="z-[10000] flex max-h-[80vh] flex-col gap-3 border-sr-panel-line bg-sr-panel text-sr-text sm:max-w-md"
        data-testid="sr-browse-all-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-[18px] font-bold text-sr-text">Browse all services</DialogTitle>
          <DialogDescription className="text-[13px] text-sr-muted">
            {multiple
              ? 'Check every service you want to see. Services you add are kept in your Favorite services.'
              : 'Pick one service to see. It\u2019s kept in your Favorite services for next time.'}
          </DialogDescription>
        </DialogHeader>
        {!multiple && (
          <p data-testid="sr-browse-all-combine-hint" className="m-0 rounded-[8px] border border-sr-gold/45 bg-sr-gold/[.08] px-[10px] py-[7px] text-[12.5px] font-semibold text-[#f3dc9a]">
            Select one at a time or combine services with a{' '}
            <Link to="/app/upgrade" onClick={onClose} className="font-extrabold text-sr-gold underline underline-offset-2 hover:text-[#ffd75e]">
              Premiere
            </Link>{' '}
            membership.
          </p>
        )}
        <div className="relative shrink-0">
          <Search size={14} className="pointer-events-none absolute left-[10px] top-1/2 -translate-y-1/2 text-sr-muted" aria-hidden />
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search services…"
            data-testid="sr-browse-all-search-input"
            className="w-full rounded-[7px] border border-sr-field-line bg-sr-field py-[7px] pl-[30px] pr-[10px] text-[13px] text-sr-text placeholder:text-sr-placeholder focus:outline-none focus-visible:ring-2 focus-visible:ring-sr-gold"
          />
        </div>
        <div className="flex-1 space-y-[2px] overflow-y-auto pr-1" data-testid="sr-browse-all-list" role={multiple ? 'group' : 'radiogroup'}>
          {filtered.length === 0 && <p className="py-4 text-center text-[13px] text-sr-muted">No matches.</p>}
          {filtered.map((p) => {
            const on = draft.includes(p.id);
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => tick(p.id)}
                role={multiple ? 'checkbox' : 'radio'}
                aria-checked={on}
                data-testid={`sr-browse-all-row-${p.id}`}
                className={`flex w-full items-center gap-[10px] rounded-[8px] px-[8px] py-[7px] text-left text-[14px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sr-gold ${
                  on ? 'bg-sr-gold/15 text-sr-gold' : 'text-sr-text hover:bg-white/5'
                }`}
              >
                <span
                  aria-hidden
                  className={`grid h-[18px] w-[18px] shrink-0 place-items-center border ${multiple ? 'rounded-[4px]' : 'rounded-full'} ${
                    on ? 'border-sr-gold bg-sr-gold text-sr-bg' : 'border-sr-field-line'
                  }`}
                >
                  {on && <Check size={12} strokeWidth={3} />}
                </span>
                <span className="grid h-[26px] w-[26px] shrink-0 place-items-center overflow-hidden rounded-[6px]">
                  {renderProviderBadge ? renderProviderBadge({ provider: p, variant: 'tile', selected: on }) : <DefaultProviderTile provider={p} />}
                </span>
                <span className="flex-1 truncate">{p.name}</span>
              </button>
            );
          })}
        </div>
        <div className="flex shrink-0 items-center justify-between gap-3 border-t border-sr-panel-line pt-3">
          <button
            type="button"
            onClick={() => setDraft([])}
            data-testid="sr-browse-all-clear"
            className="text-[13px] font-semibold text-sr-muted hover:text-sr-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sr-gold"
          >
            Clear
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              data-testid="sr-browse-all-cancel"
              className="rounded-[8px] border border-sr-field-line px-[14px] py-[7px] text-[13px] font-semibold text-sr-text hover:border-sr-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sr-gold"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onApply(draft)}
              data-testid="sr-browse-all-apply"
              className="rounded-[8px] bg-sr-gold px-[14px] py-[7px] text-[13px] font-bold text-sr-bg hover:bg-[#ffd75e] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sr-gold"
            >
              {applyLabel}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


/* ------------------------------------------------------------------ year -- */

function NativeSelect({ value, onChange, label, disabled, children }) {
  return (
    <div className="relative">
      <select aria-label={label} value={value} onChange={onChange} disabled={disabled} className={`${selectShell} appearance-none pr-8 disabled:cursor-not-allowed`}>
        {children}
      </select>
      <ChevronDown size={16} className="pointer-events-none absolute right-[10px] top-1/2 -translate-y-1/2 text-sr-text" aria-hidden />
    </div>
  );
}

export function YearRange({ yearFrom, yearTo, onChange }) {
  const years = yearOptions();
  const update = (from, to) => {
    // Keep the range valid: swap if the user inverts it.
    if (from && to && from > to) [from, to] = [to, from];
    onChange({ yearFrom: from, yearTo: to });
  };
  return (
    <div className="grid grid-cols-2 gap-[10px]">
      <NativeSelect label="Earliest release year" value={yearFrom ?? ''} onChange={(e) => update(e.target.value ? Number(e.target.value) : null, yearTo)}>
        <option value="">Any</option>
        {years.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </NativeSelect>
      <NativeSelect label="Latest release year" value={yearTo ?? ''} onChange={(e) => update(yearFrom, e.target.value ? Number(e.target.value) : null)}>
        <option value="">Any</option>
        {years.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </NativeSelect>
    </div>
  );
}

/* ---------------------------------------------------------------- rating -- */

/**
 * Single-thumb rating FLOOR, not a two-handle range — one circle that
 * moves right to raise the minimum (1+, 2+, 3+ ... up to 10). There's no
 * separate ceiling to set, so there's no second thumb and no tie/overlap
 * case to handle at all; this replaces an earlier two-thumb version (built
 * on the same Radix primitive) that members still found fiddly even after
 * the overlap bug itself was fixed — one control is simply easier to get
 * right every time. `ratingMax` always stays pinned at `RATING_MAX`
 * (nothing in the UI can lower it anymore); kept in the filters model only
 * for backward compatibility with old saved searches/URLs that still carry
 * an `rmax`.
 */
export function RatingRange({ ratingMin, onChange }) {
  const pct = ((ratingMin - RATING_MIN) / (RATING_MAX - RATING_MIN)) * 100;
  const label = ratingMin <= RATING_MIN ? 'Any' : ratingMin >= RATING_MAX ? fmtRating(RATING_MAX) : `${fmtRating(ratingMin)}+`;
  const thumbClass =
    'block h-[19px] w-[19px] rounded-full bg-[#ecebe8] shadow-[0_1px_4px_rgba(0,0,0,0.6)] cursor-grab ' +
    'active:cursor-grabbing focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sr-gold focus-visible:ring-offset-2 focus-visible:ring-offset-black/40';

  return (
    <div className="flex h-[22px] items-center gap-[12px]">
      <SliderPrimitive.Root
        className="sr-range relative flex h-[22px] flex-1 touch-none select-none items-center"
        min={RATING_MIN}
        max={RATING_MAX}
        step={RATING_STEP}
        value={[ratingMin]}
        onValueChange={([v]) => onChange({ ratingMin: v, ratingMax: RATING_MAX })}
      >
        <SliderPrimitive.Track className="relative h-[6px] w-full grow overflow-hidden rounded-full" style={{ background: 'var(--sr-track-off)' }}>
          {/* Filled from the thumb to the right edge — shrinks as the floor
              rises, so the bar always shows roughly how much of the scale
              still qualifies. */}
          <div aria-hidden className="absolute inset-y-0 right-0 rounded-full" style={{ left: `${pct}%`, background: 'var(--sr-gold)' }} />
        </SliderPrimitive.Track>
        <SliderPrimitive.Thumb aria-label="Minimum rating" aria-valuetext={`${label === 'Any' ? 'Any rating' : label} minimum`} className={thumbClass} />
      </SliderPrimitive.Root>
      <span className="w-[52px] shrink-0 text-right text-[13px] font-medium tabular-nums text-sr-text" aria-live="polite">
        {label}
      </span>
    </div>
  );
}

/* ------------------------------------------------- availability / social -- */

export function ToggleOption({ gateKey, gate, checked, onChange, onLocked, hint, showHint = true, disabledReason, children }) {
  const state = gate(gateKey);
  const blocked = state.disabled || Boolean(disabledReason);
  const note = disabledReason && !state.disabled ? { reason: disabledReason } : state;
  return (
    <div className="flex items-center gap-2" title={hint ?? undefined}>
      <Checkbox checked={checked} disabled={blocked} onChange={(on) => (state.locked ? onLocked?.() : onChange(on))}>
        {children}
      </Checkbox>
      {note.reason ? <GateNote state={note} /> : hint && showHint ? <span className="ml-auto shrink-0 text-[11px] text-sr-faint">{hint}</span> : null}
    </div>
  );
}

export function UnwatchedToggle({ filters, patch, gate, onLocked }) {
  return (
    <ToggleOption gateKey="unwatched" gate={gate} checked={filters.unwatched} onChange={(unwatched) => patch({ unwatched })} onLocked={onLocked}>
      Hide titles I&apos;ve watched
    </ToggleOption>
  );
}

export function HideWatchingToggle({ filters, patch, gate, onLocked }) {
  return (
    <ToggleOption gateKey="hideWatching" gate={gate} checked={filters.hideWatching} onChange={(hideWatching) => patch({ hideWatching })} onLocked={onLocked}>
      Hide titles I&apos;m watching
    </ToggleOption>
  );
}

/** The three circle filters. Hints spell out the agreed definitions. */
export function CircleControls({ filters, patch, gate, onLocked, showHints = true }) {
  return (
    <div>
      {CIRCLE_FILTERS.map((c) => (
        <ToggleOption
          key={c.id}
          gateKey={c.id}
          gate={gate}
          checked={filters[c.id]}
          onChange={(on) => patch({ [c.id]: on })}
          onLocked={onLocked}
          hint={c.hint}
          showHint={showHints}
        >
          {c.label}
        </ToggleOption>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------- advanced -- */

function Field({ label, state, children }) {
  return (
    <div className={`flex h-full flex-col ${state?.disabled ? 'opacity-45' : ''}`}>
      {/* Label and gate note always stack (not GateNote's usual same-line +
          ml-auto, which only suits wider rows). flex-1 + justify-end makes
          this block grow to fill whatever the grid row's height is and
          bottom-align its own text just above the control — so a field
          whose note wraps to 2-3 lines (e.g. "TV ratings not available
          yet" in the narrow sidebar column) and a note-less neighbour in
          the same row still land their controls on the same line, instead
          of a fixed line-count guess that only worked at some widths. */}
      <div className="mb-[5px] flex flex-1 flex-col justify-end gap-[2px] text-[12px] leading-tight text-sr-muted">
        <span>{label}</span>
        {state?.reason && (
          <span className="flex items-center gap-1 text-[11px] font-medium text-sr-faint">
            {state.locked && <Lock size={11} className="text-sr-gold" aria-hidden />}
            {state.reason}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

/**
 * Advanced: minimum number of ratings, runtime, original language, age rating,
 * and "hide titles not yet released". Each one is gated by its own capability.
 */
export function AdvancedControls({ filters, patch, gate, onLocked, languages }) {
  const g = {
    runtime: gate('runtime'),
    language: gate('language'),
    ageRating: gate('ageRating'),
  };
  const guard = (state, fn) => (e) => (state.locked ? onLocked?.() : fn(e));
  return (
    <div className="space-y-[10px] pt-[2px]">
      <div className="grid grid-cols-2 gap-[10px]">
        <Field label="Length" state={g.runtime.reason ? g.runtime : null}>
          <NativeSelect label="Length" value={filters.runtime} disabled={g.runtime.disabled} onChange={guard(g.runtime, (e) => patch({ runtime: e.target.value }))}>
            {RUNTIME_OPTIONS.map((o) => (
              <option key={o.id} value={o.id}>
                {o.id === 'any' ? 'Any' : o.label}
              </option>
            ))}
          </NativeSelect>
        </Field>
        <Field label="Language" state={g.language.reason ? g.language : null}>
          <NativeSelect label="Original language" value={filters.language} disabled={g.language.disabled} onChange={guard(g.language, (e) => patch({ language: e.target.value }))}>
            <option value="">Any</option>
            {languages.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </NativeSelect>
        </Field>
        <Field label="Age rating" state={g.ageRating.reason ? g.ageRating : null}>
          <NativeSelect label="Age rating" value={filters.ageMax} disabled={g.ageRating.disabled} onChange={guard(g.ageRating, (e) => patch({ ageMax: e.target.value }))}>
            {AGE_OPTIONS.map((o) => (
              <option key={o.id} value={o.id}>
                {o.id === 'any' ? 'Any' : o.label}
              </option>
            ))}
          </NativeSelect>
        </Field>
      </div>
      <ToggleOption gateKey="releasedOnly" gate={gate} checked={filters.releasedOnly} onChange={(releasedOnly) => patch({ releasedOnly })} onLocked={onLocked}>
        Hide titles not yet released
      </ToggleOption>
    </div>
  );
}

/** Explains, in the member's own words, why a vote-count floor matters —
 * same core message as the in-context "raise the floor to 100+" rating-sort
 * nudge, just always available here instead of only appearing reactively. */
// Shown from the ⓘ next to "Minimum # of ratings" (DECISIONS #22) — explains
// why the default is 25 so members can choose their own number knowingly.
const MIN_VOTES_EXPLANATION = {
  title: 'Why we start at 25 ratings',
  body: [
    'A title’s average score is only as reliable as the number of people behind it. Titles with fewer than 25 ratings are often very obscure, and a handful of perfect scores can push them to the top of your results.',
    'Starting at 25 keeps results to titles enough people have actually watched.',
    'Lower it, or clear it to Any, to dig up hidden deep cuts. Raise it to 100 or more to see only well-established titles.',
  ],
};

/**
 * Minimum # of Ratings — lives ONLY inside the Rating section now (merged
 * with RatingRange above), not as its own sidebar section and not
 * duplicated inside Advanced. Also reused as-is inside Toolbar's "More
 * Filters" popover, same `filters.minVotes` either way.
 */
export function RatingCountFilter({ filters, patch, gate, onLocked }) {
  const [infoOpen, setInfoOpen] = useState(false);
  const state = gate('minVotes');
  return (
    <div className="flex items-center gap-[10px]">
      <label htmlFor="sr-min-votes-input" className={`shrink-0 text-[12px] text-sr-muted ${state.disabled ? 'opacity-45' : ''}`}>
        Minimum # of ratings
      </label>
      <input
        id="sr-min-votes-input"
        type="number"
        min="0"
        step="25"
        inputMode="numeric"
        disabled={state.disabled}
        readOnly={state.locked}
        onFocus={state.locked ? () => onLocked?.() : undefined}
        value={filters.minVotes ?? ''}
        placeholder="Any"
        onChange={(e) => (state.locked ? onLocked?.() : patch({ minVotes: e.target.value === '' ? null : Math.max(0, Number(e.target.value)) }))}
        // Deliberately NOT built from `selectShell` (it includes `w-full`) —
        // a `w-full` + `w-[90px]` clash here used to stretch this field past
        // the panel's own edge, which made the whole sidebar scroll sideways.
        className="flex h-[30px] w-[90px] shrink-0 items-center justify-between rounded-[8px] border border-sr-field-line bg-sr-field px-[11px] text-left text-[14px] text-sr-text placeholder:text-sr-muted"
      />
      <Popover
        open={infoOpen}
        onOpenChange={setInfoOpen}
        align="start"
        width={300}
        trigger={(props) => (
          <button
            type="button"
            {...props}
            aria-label="Why we start at 25 ratings"
            data-testid="sr-min-votes-info-button"
            className="grid h-[24px] w-[24px] shrink-0 place-items-center rounded-full text-sr-muted hover:text-sr-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sr-gold"
          >
            <Info size={16} aria-hidden />
          </button>
        )}
      >
        <div data-testid="sr-min-votes-info" className="space-y-[6px] text-[13px] leading-snug text-sr-text">
          <p className="m-0 font-semibold text-sr-gold">{MIN_VOTES_EXPLANATION.title}</p>
          {MIN_VOTES_EXPLANATION.body.map((t) => (
            <p key={t} className="m-0">{t}</p>
          ))}
        </div>
      </Popover>
      <GateNote state={state} />
    </div>
  );
}

export function AvailabilityControl({ filters, patch }) {
  const theatersOnly = filters.inTheaters && !filters.atHome;
  return (
    <div className="space-y-[7px]" data-testid="sr-availability-control">
      <Checkbox checked={!!filters.atHome} onChange={(v) => patch({ atHome: v })} testId="sr-avail-home-checkbox">
        Streaming at home
      </Checkbox>
      <Checkbox checked={!!filters.inTheaters} onChange={(v) => patch({ inTheaters: v })} testId="sr-avail-theaters-checkbox">
        In theaters
      </Checkbox>
      {theatersOnly && <p className="m-0 pl-[23px] text-[12px] leading-snug text-sr-muted">Movies only · released in the last 45 days</p>}
    </div>
  );
}
